import {
  Button,
  DialogRoot,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogActionTrigger,
  VStack,
  Textarea,
  Text,
  Box,
} from '@chakra-ui/react';
import React from 'react';

import { CenteredDialogContent } from '#src/components/ui/centered-dialog-content';
import {
  EditChatModelField,
  useAskSubmit,
  useDocumentEditChatModelState,
} from '#src/pages/document/prompt-command';

export interface AskAiModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Workspace Q&A: runs ask-document-prompt without documentId (semantic search across documents).
 */
export function AskAiModal({ open, onOpenChange }: AskAiModalProps) {
  const [prompt, setPrompt] = React.useState('');
  const { chatModelId, setChatModelId, modelOptions } =
    useDocumentEditChatModelState();

  const {
    handleAskSubmit,
    askResult,
    clearAskResult,
    disabled,
    processingPrompt,
  } = useAskSubmit({
    documentId: undefined,
    isNewDocument: false,
    chatModelId,
    askMode: 'workspace',
  });

  const handleApply = React.useCallback(async () => {
    const text = prompt.trim();
    if (!text) return;
    setPrompt('');
    await handleAskSubmit(text);
  }, [prompt, handleAskSubmit]);

  const handleClose = React.useCallback(() => {
    setPrompt('');
    clearAskResult();
    onOpenChange(false);
  }, [onOpenChange, clearAskResult]);

  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => {
        if (!e.open) handleClose();
      }}
      size="md"
    >
      <CenteredDialogContent>
        <DialogHeader>
          <DialogTitle>Ask AI</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack gap={3} align="stretch">
            <Text fontSize="sm" color="fg.muted">
              Ask a question about your workspace. Answers use semantic search
              across your documents (indexed content).
            </Text>
            <EditChatModelField
              chatModelId={chatModelId}
              setChatModelId={setChatModelId}
              modelOptions={modelOptions}
              disabled={disabled}
            />
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g., What are all my open todos?"
              rows={4}
              resize="vertical"
              minH="100px"
              disabled={disabled}
            />
            {askResult != null && (
              <Box
                p={3}
                borderRadius="md"
                bg="bg.muted"
                borderWidth="1px"
                borderColor="border"
                maxH="240px"
                overflowY="auto"
              >
                <Text fontSize="sm" whiteSpace="pre-wrap">
                  {askResult}
                </Text>
              </Box>
            )}
          </VStack>
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
          </DialogActionTrigger>
          <Button
            colorScheme="purple"
            onClick={handleApply}
            disabled={!prompt.trim() || disabled}
            loading={processingPrompt}
            loadingText="Asking..."
          >
            Ask
          </Button>
        </DialogFooter>
      </CenteredDialogContent>
    </DialogRoot>
  );
}
